import type { LandingTimberPriceSeries } from "./useLandingTimberPriceSeries";
import type { Language } from "@/i18n";

export function assortmentLabels(locale: Language): {
  pine: string;
  pulp: string;
} {
  const labels: Record<Language, { pine: string; pulp: string }> = {
    fi: { pine: "Mäntytukki", pulp: "Mäntykuitupuu" },
    en: { pine: "Pine sawlog", pulp: "Pine pulpwood" },
    sv: { pine: "Talltimmer", pulp: "Tallmassaved" },
    es: { pine: "Troza de pino", pulp: "Madera de pino para pasta" },
  };
  return labels[locale];
}

export function timberPriceMarketingCopy(locale: Language) {
  const copy: Record<
    Language,
    {
      title: string;
      summary: string;
      contextSuffix: string;
      latest: string;
      periodChange: string;
      takeaway: string;
      explanation: string;
    }
  > = {
    fi: {
      title: "Myydäkö puuta nyt vai odottaa?",
      summary:
        "Seuraa oman alueesi puun hintojen kehitystä kolmen vuoden ajalta ja vertaa nykyhintoja aiempiin.",
      contextSuffix: "Pystykauppa · mäntytukki ja mäntykuitupuu",
      latest: "Uusin viikkohinta",
      periodChange: "Muutos näytetyllä jaksolla",
      takeaway:
        "Näet alueesi mäntytukin ja mäntykuitupuun hintakehityksen, kun harkitset puukaupan ajankohtaa.",
      explanation: "Mitä tämä merkitsee sinulle",
    },
    en: {
      title: "Prices in your region. Better evidence for a timber sale.",
      summary:
        "See the three-year history for the forest property's price region and the period used in the valuation.",
      contextSuffix: "Standing sale · pine sawlog and pulpwood",
      latest: "Latest weekly price",
      periodChange: "Change over the shown period",
      takeaway:
        "There is no region to choose or market table to interpret: see the regional price level, week, and assortments selected for the property.",
      explanation: "What this means for you",
    },
    sv: {
      title: "Priser i din region. Bättre underlag för virkesaffären.",
      summary:
        "Se den faktiska utvecklingen under tre år i skogsfastighetens prisregion och perioden som används i värderingen.",
      contextSuffix: "Rotförsäljning · talltimmer och tallmassaved",
      latest: "Senaste veckopris",
      periodChange: "Förändring under perioden",
      takeaway:
        "Du behöver inte välja region eller tolka marknadstabeller: se prisnivån, veckan och sortimenten som valts för fastigheten.",
      explanation: "Vad det betyder för dig",
    },
    es: {
      title: "Precios de tu región. Mejores datos para vender madera.",
      summary:
        "Consulta la evolución real de tres años en la región de precios de la finca y el periodo usado en la valoración.",
      contextSuffix: "Venta en pie · troza y madera para pasta de pino",
      latest: "Último precio semanal",
      periodChange: "Cambio durante el periodo",
      takeaway:
        "No tienes que elegir la región ni interpretar tablas de mercado: consulta el nivel de precios, la semana y los surtidos seleccionados para la finca.",
      explanation: "Qué significa para ti",
    },
  };
  return copy[locale];
}

export function priceMovement(
  points: LandingTimberPriceSeries["points"],
  dataKey: "N1" | "N4",
): { latest: number; change: number; changePercent: number } | null {
  const values = points
    .map((point) => point[dataKey])
    .filter(
      (value): value is number =>
        typeof value === "number" && Number.isFinite(value),
    );
  if (values.length < 2) return null;
  const first = values[0];
  const latest = values[values.length - 1];
  if (first === 0) return null;
  return {
    latest,
    change: latest - first,
    changePercent: ((latest - first) / first) * 100,
  };
}
